import React from 'react';
import { useRef, useState, useEffect } from 'react';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { EsResetWrapper } from '../EsResetPassword/EsResetWrapper';
import { FormControl, Box, Button, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useEsHover } from '../Animation/useEsHover';
import { en_config } from '../../config';
import { CustomTextField } from '../TextInput/CustomTextField';
import { strings } from '../../constants/strings';
import { Spinner } from '@chakra-ui/react';
import { GenericModal } from '../GenericModal/GenericModal';
import { AudmeQRCodeStep } from '../EsSignupForm/AudmeQRCodeStep';

let currrentOtpIndex: number = 0;

interface IOtpProps {
    onClickBack: () => void;
    onChange: (otp: string[]) => void;
    onResendOtp: () => void | Promise<unknown>;
    otpCodeCount?: number;
    onSubmit?: () => void;
    loading?: boolean;
    resendInProgress?: boolean;
    label?: string;
    buttonString?: string;
    hideBackButton?: boolean;
    style?: React.CSSProperties;
    verificationChannel?: 'sms' | 'whatsapp' | 'audme' | 'email';
    subtitleMessage?: string;
    onAlternateMethodClick?: () => void;
    audmeData?: {
        token?: string;
        qrCode?: string;
        deeplink?: string;
    } | null;
}

const EsVerifyCode: React.FC<IOtpProps> = ({
    onClickBack,
    onChange,
    onResendOtp,
    otpCodeCount,
    onSubmit,
    loading,
    resendInProgress,
    label,
    buttonString,
    hideBackButton,
    style,
    verificationChannel,
    subtitleMessage,
    onAlternateMethodClick,
    audmeData,
}) => {
    const effectiveOtpCount = otpCodeCount ?? en_config.TWO_FA_LENGTH;
    const [otp, setOtp] = useState<string[]>(new Array(effectiveOtpCount).fill(''));
    const [activeOTPIndex, setActiveOTPIndex] = useState<number>(0);
    const [seconds, setSeconds] = useState(30); // Set initial countdown value
    const [isActive, setIsActive] = useState(true);
    const [showAudmeSetupModal, setShowAudmeSetupModal] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    const handleOnChange = ({ target }: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = target;
        const newOTP: string[] = [...otp];
        newOTP[currrentOtpIndex] = value.substring(value.length - 1);

        if (!value) setActiveOTPIndex(currrentOtpIndex - 1);
        else setActiveOTPIndex(currrentOtpIndex + 1);

        if (onChange) {
            onChange(newOTP);
        }

        setOtp(newOTP);
    };

    const handleOnKeyDown = ({ key }: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        currrentOtpIndex = index;

        if (key === 'Backspace') {
            if (otp[index]) {
                // If current box has a value, clear it
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            } else {
                // If already empty, move focus to previous input
                setActiveOTPIndex(currrentOtpIndex - 1);
            }
        }
    };

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, effectiveOtpCount);
        if (!pasted) return;
        const newOTP = [...otp];
        pasted.split('').forEach((char, i) => {
            newOTP[i] = char;
        });
        setOtp(newOTP);
        onChange?.(newOTP);
        setActiveOTPIndex(Math.min(pasted.length, effectiveOtpCount - 1));
    };
    const { handleMouseEnter, handleMouseLeave, isHover } = useEsHover();

    useEffect(() => {
        inputRef.current?.focus();
    }, [activeOTPIndex]);

    useEffect(() => {
        let interval: string | number | NodeJS.Timer | null | undefined = null;

        if (isActive && seconds > 0) {
            interval = setInterval(() => {
                setSeconds(prevSeconds => prevSeconds - 1);
            }, 1000);
        } else if (seconds === 0) {
            setIsActive(false);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isActive, seconds]);

    const handleResendClick = () => {
        if (!resendInProgress) {
            if (onResendOtp) {
                if (onResendOtp instanceof Promise) {
                    onResendOtp()
                        ?.then(() => {
                            setSeconds(30);
                            setIsActive(true);
                        })
                        .catch(e => {});
                } else {
                    onResendOtp();
                    setSeconds(30);
                    setIsActive(true);
                }
            }
        }
    };
    const { themeStyle, darkModeEnabled } = useThemeContext();
    const styles = dynamicStyles(themeStyle, isHover, loading, darkModeEnabled);

    const subtitle = subtitleMessage
        ? subtitleMessage
        : verificationChannel === 'audme'
        ? 'Enter the code generated on the audme app'
        : `We just sent you a verification code. Check your ${verificationChannel}`;

    return (
        <EsResetWrapper title={label ?? 'Enter OTP'} subtitle={subtitle}>
            <FormControl style={{ width: '100%', minHeight: '300px', ...style }}>
                <Box
                    sx={{
                        marginTop: '40px',
                        marginBottom: '40px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: '8px', sm: '18px' },
                    }}>
                    {otp.map((field, index) => {
                        return (
                            <CustomTextField
                                key={index}
                                onChange={handleOnChange}
                                isOTP={true}
                                textFieldProps={{
                                    InputProps: {
                                        disableUnderline: true,
                                    },
                                    inputProps: {
                                        type: 'number',
                                        inputMode: 'numeric',
                                        autoComplete: 'one-time-code',
                                        ref: index === activeOTPIndex ? inputRef : null,
                                        onKeyDown: (e: any) => handleOnKeyDown(e, index),
                                        onPaste: (e: any) => handleOnPaste(e),
                                        required: true,
                                        value: otp[index],
                                        sx: {
                                            ...styles.input,
                                            width: { xs: 42, sm: 54 },
                                            height: { xs: 48, sm: 72 },
                                            fontSize: { xs: 20, sm: 30 },
                                        },
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
                <Button
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    style={styles.button}
                    onClick={() => {
                        if (onSubmit) {
                            onSubmit();
                        } else {
                            handleMouseLeave();
                        }
                    }}
                    type={onSubmit ? 'button' : 'submit'}
                    disabled={otp.some(value => !value) || loading}>
                    {loading ? (
                        <Spinner h={4} w={4} color={theme[themeStyle].tag_white} />
                    ) : (
                        <Typography style={styles.signup}>{buttonString ?? strings.sign_in}</Typography>
                    )}
                </Button>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    {!hideBackButton && (
                        <Button style={styles.backButton} onClick={onClickBack} type="button">
                            <Typography style={styles.backText}>{strings.go_back}</Typography>
                        </Button>
                    )}
                    {verificationChannel === 'audme' ? (
                        // For audme, show setup button and alternate method button
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                            {audmeData?.qrCode && audmeData?.deeplink && (
                                <Typography
                                    onClick={() => setShowAudmeSetupModal(true)}
                                    style={{
                                        ...styles.message,
                                        ...styles.alternateMethod,
                                        cursor: 'pointer',
                                    }}>
                                    Haven't set up audme? set up now
                                </Typography>
                            )}
                            {onAlternateMethodClick && (
                                <Typography
                                    onClick={onAlternateMethodClick}
                                    style={{
                                        ...styles.message,
                                        ...styles.alternateMethod,
                                        cursor: 'pointer',
                                    }}>
                                    or use an alternate method
                                </Typography>
                            )}
                        </Box>
                    ) : verificationChannel ? (
                        // For other channels, show countdown/resend and alternate method
                        <>
                            {!isActive && !loading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}>
                                    <Typography component={'span'} style={styles.message}>
                                        Didn't get the code? {'Click '}
                                        {resendInProgress ? (
                                            <Spinner h={4} w={4} color={theme[themeStyle].tag_white} />
                                        ) : (
                                            <span
                                                onClick={handleResendClick}
                                                style={{ ...styles.countdown, cursor: 'pointer' }}>
                                                here
                                            </span>
                                        )}{' '}
                                        to request a new one.
                                    </Typography>
                                    {onAlternateMethodClick && (
                                        <Typography
                                            onClick={onAlternateMethodClick}
                                            style={{
                                                ...styles.message,
                                                ...styles.alternateMethod,
                                                cursor: 'pointer',
                                            }}>
                                            or use an alternate method
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}>
                                    <Typography style={styles.message}>
                                        Request new code in?{' '}
                                        <span style={styles.countdown}>(00:{seconds.toString().padStart(2, '0')})</span>
                                    </Typography>
                                    {onAlternateMethodClick && (
                                        <Typography
                                            onClick={onAlternateMethodClick}
                                            style={{
                                                ...styles.message,
                                                ...styles.alternateMethod,
                                                cursor: 'pointer',
                                            }}>
                                            or use an alternate method
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </>
                    ) : null}
                </Box>
            </FormControl>
            {showAudmeSetupModal && audmeData?.qrCode && audmeData?.deeplink && (
                <GenericModal
                    open={showAudmeSetupModal}
                    handleClose={() => setShowAudmeSetupModal(false)}
                    paperStyle={{
                        minHeight: 'fit-content',
                        width: 'fit-content',
                        maxWidth: '500px',
                        height: 'fit-content',
                        padding: 20,
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        border: 'transparent',
                    }}>
                    <AudmeQRCodeStep
                        qrCode={audmeData.qrCode}
                        deeplink={audmeData.deeplink}
                        onReady={() => setShowAudmeSetupModal(false)}
                        onBack={() => setShowAudmeSetupModal(false)}
                    />
                </GenericModal>
            )}
        </EsResetWrapper>
    );
};

EsVerifyCode.defaultProps = {
    otpCodeCount: en_config.TWO_FA_LENGTH,
};
//TODO: remove redundant styles
const dynamicStyles = (colorScheme: ColorScheme, isHover: boolean, isLoading?: boolean, darkModeEnabled?: boolean) => ({
    text: {
        maxWidth: 400,
        width: '100%',
        textAlign: 'left' as const,
        fontSize: 14,
        fontStyle: 'normal' as const,
        fontWeight: '400',
        color: theme[colorScheme].form_label,
        fontFamily: FONT_ENUM.MAIN_REGULAR,
    },
    input: {
        backgroundColor: theme[colorScheme].signUpGrey,
        color: theme[colorScheme].lightWhite,
        fontSize: 30,
        textAlign: 'center' as const,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        ...(!darkModeEnabled && { border: `1px solid ${theme[colorScheme].tag_white}`, borderRadius: 3 }),
    },
    signup: {
        fontSize: 14,
        fontWeight: '600',
        fontFamily: FONT_ENUM.MAIN_BOLD,
        color: isHover ? theme[colorScheme].primary_orange : theme[colorScheme].gray_text,
    },
    button: {
        width: '100%',
        height: 44,
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 12,
        paddingBottom: 12,
        borderRadius: 100,
        maxWidth: 400,
        backgroundColor: isHover || isLoading ? theme[colorScheme].gray_text : theme[colorScheme].primary_orange,
        textTransform: 'none' as const,
        alignSelf: 'center' as const,
    },
    message: {
        maxWidth: 400,
        width: 'fit-content',
        textAlign: 'justify' as const,
        fontSize: 14,
        fontStyle: 'normal' as const,
        fontWeight: '400',
        color: theme[colorScheme].light_gray_text,
        fontFamily: FONT_ENUM.MAIN_REGULAR,
        marginTop: 20,
    },
    countdown: {
        maxWidth: 400,
        width: '100%',
        textAlign: 'justify' as const,
        fontSize: 14,
        fontStyle: 'normal' as const,
        fontWeight: '400',
        fontFamily: FONT_ENUM.MAIN_REGULAR,
        color: theme[colorScheme].primary_orange,
        marginTop: 20,
    },
    backText: {
        textAlign: 'center' as const,
        fontSize: 12,
        fontStyle: 'normal' as const,
        fontWeight: '400',
        fontFamily: FONT_ENUM.MAIN_REGULAR,
        color: theme[colorScheme].lightWhite,
    },
    backButton: {
        width: 'fit-content',
        alignSelf: 'center' as const,
        height: 'fit-content',
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 12,
        paddingBottom: 12,
        backgroundColor: theme[colorScheme].transparent,
        textTransform: 'none' as const,
        marginTop: 20,
    },
    alternateMethod: {
        color: theme[colorScheme].primary_orange,
        fontWeight: '500',
        textDecoration: 'underline',
    },
});

export { EsVerifyCode };
